import { type FC, type ReactNode, memo, useMemo } from 'react'
import styled from 'styled-components'

import BigToggle from 'components/BigToggle'
import SmallToggle from 'components/SmallToggle'
import { formatV3AssetKey } from 'utils/futures'
import { SYNTH_ICONS } from 'utils/icons'

import SUSDWhiteIcon from '../../../public/images/png/tokens/sUSD-White.png'

import { FlexDivCol } from 'components/layout/flex'
import NumericInput from './NumericInput'

const AssetSelector = memo(
	({
		small = true,
		assets,
		selectedAsset,
		onSelectAsset,
	}: {
		small: boolean
		assets: string[]
		selectedAsset: string
		onSelectAsset: (asset: string) => any
	}) => {
		const swapTokenIconMap = useMemo(
			() => ({
				...SYNTH_ICONS,
				SUSD: SUSDWhiteIcon,
			}),
			[]
		)

		return (
			<div>
				{small ? (
					<SmallToggle
						value={selectedAsset}
						options={assets}
						onOptionClick={onSelectAsset}
						iconMap={swapTokenIconMap}
						labelFormatter={formatV3AssetKey}
						type="transfer"
					/>
				) : (
					<BigToggle
						options={assets}
						value={selectedAsset}
						onOptionClick={onSelectAsset}
						iconMap={swapTokenIconMap}
						labelFormatter={formatV3AssetKey}
					/>
				)}
			</div>
		)
	}
)

type DepositInputProps = {
	value: string
	small?: boolean
	selectedAsset: string
	leftLabel?: ReactNode
	assets?: string[]
	invalid?: boolean
	onSelectAsset: (asset: string) => void
	setValue(value: string): void
}

const InputWithAssetSelector: FC<DepositInputProps> = memo(
	({ small = true, value, leftLabel, selectedAsset, assets, invalid, onSelectAsset, setValue }) => {
		return (
			<DepositInputContainer $invalid={invalid}>
				<FlexDivCol rowGap="0px">
					<DepositInputMain
						placeholder="0.00"
						height={24}
						value={value}
						onChange={(_, v) => setValue(v)}
					/>
					{leftLabel && <LeftLabel>{leftLabel}</LeftLabel>}
				</FlexDivCol>
				{assets && assets.length > 0 && (
					<AssetSelector
						small={small}
						assets={assets}
						selectedAsset={selectedAsset}
						onSelectAsset={onSelectAsset}
					/>
				)}
			</DepositInputContainer>
		)
	}
)

export default InputWithAssetSelector

const DepositInputMain = styled(NumericInput)`
	border: 0;
	background: transparent;
	box-shadow: none;
	input {
		font-size: 15px;
		font-weight: 400;
		font-feature-settings: 'tnum';
		letter-spacing: -1px;
	}
`

const DepositInputContainer = styled.div<{ $invalid?: boolean }>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	border-color: ${(props) =>
		props.$invalid
			? props.theme.colors.selectedTheme.newTheme.text.negative
			: props.theme.colors.selectedTheme.newTheme.border.style};
	border-radius: 8px;
	position: relative;
	padding: 10px;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
`

const LeftLabel = styled.div`
	font-size: 10px;
	font-weight: 400;
	padding-left: 10px;
	margin-top: -5px;
	padding-bottom: 5px;
`
